<script lang="ts">
  import { Skeleton } from '@/lib/components/ui/skeleton';
  import { Badge } from '@/lib/components/ui/badge';
  import { userDistanceUnit } from '@/Util/utils';
  import { activityChartPalette } from '@/Util/XertAm5';
  import type { MeasureRow, PeriodStats } from '../progressionApi';
  import DetailTable from './DetailTable.svelte';
  import MiniStars from './MiniStars.svelte';
  import Stat from './Stat.svelte';
  import StatCard from './StatCard.svelte';
  import { changeTone, DASH, formatChange, formatCompact, formatHours, formatMMSS, formatNumber, perWeek } from './statFormat';

  export let stats: PeriodStats | null;
  /** A newer request is in flight; the figures shown are from the previous one. */
  export let loading: boolean = false;
  export let failed: boolean = false;
  /** The athlete's fitness history over the range, for the figures that aren't sums over activities. */
  export let measures: MeasureRow[] | null = null;
  export let timezone: string = '';

  const num = (value: unknown): number => (typeof value === 'number' ? value : NaN);

  // Three cards to a row when there is room for them, otherwise one; each card's figures stay on a single line.
  const gridClass = 'grid grid-cols-1 items-start gap-3 @3xl:grid-cols-3';
  const panelClass = 'rounded-lg border border-solid border-border bg-white p-4 flex flex-col gap-3';
  const threeAcross = 'repeat(3, minmax(0, 1fr))';
  // Start and End carry their training status stars underneath, so they get a little more room.
  const withStars = 'minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(min-content, 1fr)';

  $: hasActivities = !!stats && stats.count > 0;
  $: rangeDays = stats?.range_days ?? NaN;

  // Focus at the start and end of the range comes from the daily history rather than from the
  // activities (which only have the focus of the day they were ridden), so it is not narrowed by
  // the filters. The end is the last day up to the end of the range: projected days are excluded.
  $: focusStart = focusAt(measures, stats, 'start');
  $: focusEnd = focusAt(measures, stats, 'end');
  function focusAt(rows: MeasureRow[] | null, current: PeriodStats | null, which: 'start' | 'end'): number {
    if (!rows?.length || !current) return NaN;
    const inRange = rows.filter((row) => {
      const at = Date.parse(row.start_date) / 1000;
      return at >= current.start_ts && at <= current.end_ts && typeof row.focus_strain === 'number';
    });
    const row = which === 'start' ? inRange[0] : inRange[inRange.length - 1];
    return row ? row.focus_strain! * 60 : NaN;
  }

  const dateFormat = (seconds: number) =>
    new Date(seconds * 1000).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: timezone || undefined,
    });

  $: rangeLabel = stats ? `${dateFormat(stats.start_ts)} – ${dateFormat(stats.end_ts)}` : '';
  $: tlChange = num(stats?.end_tl) - num(stats?.start_tl);
  $: tpChange = num(stats?.end_ftp) - num(stats?.start_ftp);
  $: distanceUnit = userDistanceUnit();
  const perWeekTitle = 'Per week, over the whole date range';

  // The same brown/red used elsewhere in the app for detraining and extreme training status.
  const rampLabelColor = (rampRate: number): string | undefined => {
    if (rampRate < -2) return '#984D08';
    if (rampRate > 7) return '#DA4757';
    return undefined;
  };
</script>

<div class="flex flex-col gap-3">
  <div class="flex flex-row flex-wrap items-center gap-2 text-sm text-muted-foreground">
    {#if stats}
      <span>{stats.count} {stats.count === 1 ? 'activity' : 'activities'} · {rangeLabel}</span>
      {#if stats.filtered}
        <Badge variant="secondary" title="Hours, distance, XSS, average focus and energy only count the activities matching the filters. Training Load, Threshold Power and the start and end focus are not affected.">
          Filtered
        </Badge>
      {/if}
    {/if}
    {#if failed}
      <span class="text-red-600">Couldn't refresh the period stats.</span>
    {/if}
  </div>

  <div class="@container flex flex-col gap-3">
    <section class={panelClass}>
      <h2 class="text-base font-medium m-0">Period Stats - Totals and Averages</h2>
      {#if !stats}
        <div class={gridClass}>
          {#each Array(6) as _}<Skeleton class="h-20 w-full" />{/each}
        </div>
      {:else}
        <div class={`${gridClass} transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
          <StatCard title="Hours" icon="fa-clock" color="#FF5B5B">
            <Stat label="Avg" value={hasActivities ? formatHours(stats.avg_time) : DASH} />
            <Stat label="Max" value={hasActivities ? formatHours(stats.max_time) : DASH} activity={stats.max_activities?.time ?? null} />
            <Stat label="Total" value={formatHours(stats.total_time)} />
            <Stat label="/wk" title={perWeekTitle} value={formatHours(perWeek(stats.total_time, rangeDays))} />
          </StatCard>

          <StatCard title={`Distance (${distanceUnit})`} icon="fa-route" color={activityChartPalette.speed}>
            <Stat label="Avg" value={hasActivities ? formatNumber(stats.avg_distance, 1) : DASH} />
            <Stat label="Max" value={hasActivities ? formatNumber(stats.max_distance, 1) : DASH} activity={stats.max_activities?.distance ?? null} />
            <Stat label="Total" value={formatCompact(stats.total_distance)} />
            <Stat label="/wk" title={perWeekTitle} value={formatNumber(perWeek(stats.total_distance, rangeDays), 0)} />
          </StatCard>

          <StatCard title="XSS" icon="fa-bolt" color="#9933FF">
            <Stat label="Avg" value={hasActivities ? formatNumber(stats.avg_xss, 0) : DASH} />
            <Stat label="Max" value={hasActivities ? formatNumber(stats.max_xss, 0) : DASH} activity={stats.max_activities?.xss ?? null} />
            <Stat label="Total" value={formatCompact(stats.total_xss)} />
            <Stat label="/wk" title={perWeekTitle} value={formatNumber(perWeek(stats.total_xss, rangeDays), 0)} />
          </StatCard>

          <StatCard title="Calories (kcal)" icon="fa-fire" color="#F5A623">
            <Stat label="Avg" value={hasActivities ? formatNumber(stats.avg_calories, 0) : DASH} />
            <Stat label="Max" value={hasActivities ? formatNumber(stats.max_calories, 0) : DASH} activity={stats.max_activities?.calories ?? null} />
            <Stat label="Total" value={formatCompact(stats.total_calories)} />
            <Stat label="/wk" title={perWeekTitle} value={formatNumber(perWeek(stats.total_calories, rangeDays), 0)} />
          </StatCard>

          <StatCard title="Fat (g)" icon="fa-droplet" color="#DAA520">
            <Stat label="Avg" value={hasActivities ? formatNumber(stats.avg_tgf, 0) : DASH} />
            <Stat label="Max" value={hasActivities ? formatNumber(stats.max_tgf, 0) : DASH} activity={stats.max_activities?.tgf ?? null} />
            <Stat label="Total" value={formatCompact(stats.total_tgf)} />
            <Stat label="/wk" title={perWeekTitle} value={formatNumber(perWeek(stats.total_tgf, rangeDays), 0)} />
          </StatCard>

          <StatCard title="Carbs (g)" icon="fa-wheat-awn" color="#50E3C2">
            <Stat label="Avg" value={hasActivities ? formatNumber(stats.avg_tgc, 0) : DASH} />
            <Stat label="Max" value={hasActivities ? formatNumber(stats.max_tgc, 0) : DASH} activity={stats.max_activities?.tgc ?? null} />
            <Stat label="Total" value={formatCompact(stats.total_tgc)} />
            <Stat label="/wk" title={perWeekTitle} value={formatNumber(perWeek(stats.total_tgc, rangeDays), 0)} />
          </StatCard>
        </div>
      {/if}
    </section>

    <section class={panelClass}>
      <h2 class="text-base font-medium m-0">Training Stats</h2>
      {#if !stats}
        <div class={gridClass}>
          {#each Array(3) as _}<Skeleton class="h-20 w-full" />{/each}
        </div>
      {:else}
        <div class={`${gridClass} transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
          <StatCard title="Training Load" detailsLabel="Low, High, Peak" columns={withStars} icon="fa-weight-hanging" color="#111827">
            <Stat label="Start" value={formatNumber(stats.start_tl, 1)}>
              <MiniStars slot="below" load={num(stats.start_tl)} title="Training status at the start" />
            </Stat>
            <Stat label="End" value={formatNumber(stats.end_tl, 1)}>
              <MiniStars slot="below" load={num(stats.end_tl)} title="Training status at the end" />
            </Stat>
            <Stat label="Change" value={formatChange(tlChange, 1)} tone={changeTone(tlChange, 1)} />
            <Stat label="Ramp" title="Ramp rate: change in training load per week" value={formatChange(stats.ramp_rate, 1)}>
              <span
                slot="below"
                class="text-[11px] leading-tight whitespace-normal"
                class:text-muted-foreground={!rampLabelColor(stats.ramp_rate)}
                style={rampLabelColor(stats.ramp_rate) ? `color: ${rampLabelColor(stats.ramp_rate)}` : undefined}
                title="The Improvement Rate closest to this ramp rate"
              >
                ≈ {stats.ramp_rate_label}
              </span>
            </Stat>
            <svelte:fragment slot="details" let:columns>
              <DetailTable
                {columns}
                rangeDays={rangeDays}
                rows={[
                  { label: 'Low', start: num(stats.start_ltl), end: num(stats.end_ltl) },
                  { label: 'High', start: num(stats.start_htl), end: num(stats.end_htl) },
                  { label: 'Peak', start: num(stats.start_ptl), end: num(stats.end_ptl) },
                ]}
              />
            </svelte:fragment>
          </StatCard>

          <StatCard title="Threshold Power (W)" detailsLabel="HIE, PP, LTP" icon="fa-gauge-high" color="#DAA520">
            <Stat label="Start" value={formatNumber(stats.start_ftp, 0)} />
            <Stat label="End" value={formatNumber(stats.end_ftp, 0)} />
            <Stat label="Change" value={formatChange(tpChange, 0)} tone={changeTone(tpChange, 0)} />
            <Stat label="/wk" title={perWeekTitle} value={formatChange(perWeek(tpChange, rangeDays), 1)} />
            <svelte:fragment slot="details" let:columns>
              <!-- HIE comes in joules; it is read and shown in kJ everywhere else. -->
              <DetailTable
                {columns}
                rangeDays={rangeDays}
                rows={[
                  { label: 'HIE (kJ)', start: num(stats.start_hie) / 1000, end: num(stats.end_hie) / 1000 },
                  { label: 'PP (W)', start: num(stats.start_pp), end: num(stats.end_pp) },
                  { label: 'LTP (W)', start: num(stats.start_ltp), end: num(stats.end_ltp) },
                ]}
              />
            </svelte:fragment>
          </StatCard>

          <StatCard title="Focus (m:ss)" columns={threeAcross} icon="fa-bullseye" color="#458141">
            <Stat label="Start" value={formatMMSS(focusStart)} title="Focus duration at the start of the range" />
            <Stat label="Avg" value={hasActivities && stats.avg_sfd > 0 ? formatMMSS(stats.avg_sfd) : DASH} title="Average focus duration of the activities" />
            <Stat label="End" value={formatMMSS(focusEnd)} title="Focus duration at the end of the range" />
          </StatCard>
        </div>
      {/if}
    </section>
  </div>
</div>
